import type {
  FormulaDiagnostic,
  FormulaScopeWarning,
} from '@/api/customFields/formulaFields.interface';
import type { FormulaIssue } from './formulaEngine';

// Shown where a formula runs but produces nothing to store.
export const NO_RESULT = '-';

const FALLBACK = "This formula can't run. Check it and try again.";

/**
 * Daniel's copy, Oct 2026. Several of his sentences name the offending value —
 * the field, the function, the limit — and the server sends none of those on
 * these codes: `diagnostics` carries `code`, `position` and, on four codes, a
 * `detail`. Where the value is missing the sentence is kept and the clause
 * naming it dropped, rather than printing a placeholder at an admin. The ones
 * that would read better with it are listed for the backend.
 */
const DIAGNOSTIC_MESSAGES: Record<string, string> = {
  err_formula_syntax: 'This formula has a syntax error. Fix it to continue.',
  err_formula_unknown_field:
    'This formula names a field that does not exist. Use an existing field, or create it first.',
  err_formula_dangling_reference:
    'This formula reads a field that has been deleted. Remove it from the formula, or pick another field.',
  err_formula_self_reference: "This field can't reference itself",
  err_formula_circular_reference:
    'This field and another reference each other, so neither can be calculated. Remove one of the references.',
  err_formula_chain_too_deep:
    'This formula reads through too many linked fields. Shorten the chain to continue.',
  err_formula_too_long: 'This formula is too long. Shorten it to continue.',
  err_formula_too_many_references:
    'This formula references too many fields. Remove some to continue.',
  err_formula_too_deeply_nested:
    'This formula is nested too deeply. Flatten it to continue.',
  err_formula_unknown_function:
    'This formula uses a function that does not exist. Choose one from the function list.',
  err_formula_unknown_operator:
    'This formula uses an operator that does not exist. Choose one from the operator list.',
  err_formula_division_by_zero:
    'This formula divides by zero. Make sure the divisor is never zero.',
  err_formula_not_computable:
    'This formula has nothing to calculate. Add a field or a value.',
  err_formula_result_type:
    "This formula produces a value this field can't hold. Change the formula or the field type.",
  // Arithmetic over a field that holds no number. The result type is right —
  // adding and multiplying always answer a number — so only the operands give
  // it away, which is why this has a code of its own.
  err_formula_operand_type:
    'This formula does arithmetic on something that is not a number. Only numbers can be added, subtracted, multiplied or divided.',
  err_formula_unsupported_construct:
    "This formula uses something that can't be calculated. Remove the unsupported part to continue.",
  // A dropdown's options are created from the formula's own literals, so a
  // result that is built rather than written — upper({role}), a concat, a
  // field on its own — creates no options and would resolve to nothing on
  // every offer.
  err_formula_select_result_not_literal:
    'A dropdown\u2019s formula must return one of its option names, like if(\u2026, "Senior", "Mid")',
  // No edit to the formula can resolve this one — two fields answer to the
  // same token, so a field has to be renamed.
  err_formula_fields_ambiguous:
    'Two fields share this name. Rename one before using it here.',
  err_formula_field_inaccessible:
    'This formula reads a field that shares no offices or departments with this one, so this value can never be calculated. Add it to one of this field\u2019s offices or departments, or use a different field.',
  // Daniel asked why a multi-select is refused at all. Until that
  // is answered the refusal stands, in his register.
  err_formula_target_multi:
    "A field that holds several values can't be calculated",
  err_formula_target_not_computable:
    "This field type can't be calculated. Use a number, currency, date, or text field.",
  // Both of these are an admin's setting, not a box the recruiter left empty.
  // The server gives them their own codes for exactly that reason: as
  // `err_formula_not_computable` they read as "waiting on an input" and sent
  // the recruiter to fill in a field that was already filled (CORE-5630 #18).
  err_formula_source_unreadable:
    'This formula reads something it is not allowed to see, like a confidential requisition. An admin has to open it up, or the formula has to read a different field.',
  err_formula_currency_mismatch:
    'This formula reads a pay band set in a currency this offer does not use. Set the band in the offer\u2019s currency, or read a different field.',
  err_formula_fields_disabled:
    'Formula fields are turned off for this account. Ask your admin to enable them.',
  err_formula_result_unstorable:
    "This formula can produce a value this field can't hold.",
  err_formula_date_format_invalid:
    "This date format has something in it that can't be filled in",
  err_formula_invalid_unit: "This function doesn't know that unit",
  err_formula_date_needs_format:
    'A date has to be formatted before it can be used as text. Wrap it in format_date().',
};

// A list read as prose: "week, weeks or month".
const asList = (items: string[]) =>
  items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;

const quotedList = (items: unknown) =>
  Array.isArray(items)
    ? asList(items.map((item) => `\u201c${String(item)}\u201d`))
    : '';

/**
 * A date format carrying something the formatter will not substitute, e.g.
 * "DD/MM/YYYY" — only `dd` and `yyyy` are filled in, so the rest is printed
 * into the letter as written. Naming the offending runs is the whole value of
 * the message, and there is no position to underline (core#6190).
 *
 * Deliberately says nothing about the format being correct otherwise: the
 * check catches mistyped tokens only, and token letters inside ordinary words
 * are substituted before it runs.
 */
const describeDateFormat = (detail?: Record<string, unknown>) => {
  const unknown = quotedList(detail?.unknown);
  if (!unknown) return DIAGNOSTIC_MESSAGES.err_formula_date_format_invalid;
  const many = Array.isArray(detail?.unknown) && detail.unknown.length > 1;
  // Named, because one of these arrives per offending call: two format_date
  // calls give two refusals, and without the format they read identically
  // (core#6190, revised).
  const format = detail?.format ? ` \u201c${String(detail.format)}\u201d` : '';
  return `The date format${format} cannot fill in ${unknown}, so ${
    many ? 'they are' : 'it is'
  } printed as written`;
};

/**
 * A unit the function does not take. The accepted list comes off the payload,
 * never from here: the same code is raised for dateadd and datediff with a
 * wider set that includes days, where start_day answers nothing (core#6190).
 */
const describeInvalidUnit = (detail?: Record<string, unknown>) => {
  const unit = detail?.unit === undefined ? '' : String(detail.unit);
  const fn = detail?.function === undefined ? '' : String(detail.function);
  const expects = quotedList(detail?.expects);
  if (!unit || !expects) return DIAGNOSTIC_MESSAGES.err_formula_invalid_unit;
  const subject = fn ? `\u201c${fn}\u201d` : 'This function';
  return `${subject} does not take \u201c${unit}\u201d \u2014 it takes ${expects}`;
};

/**
 * A date handed to a function that takes text. The server names the function,
 * which argument of it, and the call to wrap the date in — `concat` at
 * argument 2, expecting `format_date` — so the sentence can point at the one
 * the author has to change rather than at the formula as a whole.
 */
const describeNeedsFormat = (detail?: Record<string, unknown>) => {
  const fn = detail?.function === undefined ? '' : String(detail.function);
  const expects = detail?.expects === undefined ? '' : String(detail.expects);
  const argument =
    typeof detail?.argument === 'number' ? detail.argument : undefined;
  if (!fn || !expects) return DIAGNOSTIC_MESSAGES.err_formula_date_needs_format;
  const where =
    argument === undefined ? 'a date' : `the date at argument ${argument}`;
  return `\u201c${fn}\u201d takes text, so ${where} has to be formatted first. Wrap it in ${expects}().`;
};

// What each kind will actually accept, for the literal it refused. The server
// type-checked the formula but not the value it answers with: `url`,
// `enum_boolean` and `string` all type as text, so `if(c, "YES", "NO")` on a
// Yes/No field read as valid and then broke the offer save.
const describeUnstorable = (detail?: Record<string, unknown>) => {
  const value = detail?.value === undefined ? '' : String(detail.value);
  const quoted = value ? `“${value}”` : 'That answer';

  switch (String(detail?.kind ?? '')) {
    case 'enum_boolean':
      return `A Yes/No field can only hold Y or N, but the formula can produce ${quoted}.`;
    case 'url':
      return `${quoted} is not a valid link. Make the formula return a full URL, like https://kula.ai.`;
    case 'string':
      // The count is the refused answer's own length; the limit is the
      // column's, which is the one fixed number in this file.
      return `This value is ${value.length} characters, but the field holds 255. Shorten the formula\u2019s result.`;
    default:
      return DIAGNOSTIC_MESSAGES.err_formula_result_unstorable;
  }
};

// The three kinds the language reasons about, in the words a person uses.
const TYPE_WORDS: Record<string, string> = {
  numeric: 'a number',
  string: 'text',
  logical: 'yes or no',
};

/**
 * "This formula gives a value this field cannot hold" is true and unhelpful:
 * it never says what the field holds or what the formula works out. The server
 * sends both, so the message names them.
 */
/**
 * `=` is the one that catches people out: typed as assignment, it compares,
 * so `{salary} * 20 = {bonus}` on a Number field is refused for producing yes
 * or no and the author cannot see why (Antonio, CORE-5598). Where that is
 * what happened, the sentence says which operator did it.
 */
const COMPARISONS = ['!=', '<>', '>=', '<=', '=', '>', '<'];

const comparisonIn = (formula?: string) =>
  COMPARISONS.find((operator) =>
    // Longest first, so `>=` is never reported as `>`.
    (formula ?? '').includes(operator)
  );

// Names the half that cannot be arithmetic, which is the whole difficulty:
// {Job title} * 2 looks like a number because the multiplication is, and it
// used to save and then answer blank on every offer.
const describeOperandType = (detail?: Record<string, unknown>) => {
  const got = TYPE_WORDS[String(detail?.actual ?? '')];
  if (!got) return DIAGNOSTIC_MESSAGES.err_formula_operand_type;

  return `This formula does arithmetic on ${got}. Only numbers can be added, subtracted, multiplied or divided.`;
};

const describeResultType = (
  detail?: Record<string, unknown>,
  formula?: string,
  kind?: string
) => {
  // A Yes/No field stores "Y" or "N", so the server asks for text and reports
  // a comparison as the wrong type. Said in those words the sentence is a
  // riddle: it tells the author their Yes/No field holds text, and that the
  // formula produces yes or no — which is what they wanted (QA round 3).
  if (kind === 'enum_boolean' && String(detail?.expected) === 'string') {
    return 'A Yes/No field stores “Y” or “N”, so the formula has to answer with one of those. Wrap it, like IF(\u2026, "Y", "N").';
  }
  const wanted = TYPE_WORDS[String(detail?.expected ?? '')];
  const got = TYPE_WORDS[String(detail?.actual ?? '')];
  if (!wanted) return DIAGNOSTIC_MESSAGES.err_formula_result_type;
  // A comparison is the usual reason a formula answers yes or no by accident.
  const operator = got === 'yes or no' ? comparisonIn(formula) : undefined;
  if (operator)
    return `This field holds ${wanted}, but \u201c${operator}\u201d compares two values, so the formula produces yes or no. Remove the comparison, or change the field type.`;
  // `conflicting` means the branches disagree with each other, so there is no
  // single "got" to name — say which branch is the odd one out instead.
  if (!got) {
    return `This field holds ${wanted}, but the formula doesn\u2019t always produce that type. Make sure it returns ${wanted}.`;
  }
  return `This field holds ${wanted}, but the formula produces ${got}. Change the formula or the field type.`;
};

// An unmapped code is still a real failure, so it reads as one rather than
// showing the recruiter a raw `err_` string.
export const describeDiagnostic = (
  diagnostic?: FormulaDiagnostic,
  // The formula the verdict is about, where the caller has it: one message
  // reads better when it can name the operator that caused the refusal.
  formula?: string,
  // The field the formula is for. Only a Yes/No field needs it, and only to
  // keep the result-type sentence from describing it as text.
  kind?: string
) => {
  if (diagnostic?.code === 'err_formula_result_type') {
    return describeResultType(diagnostic.detail, formula, kind);
  }
  if (diagnostic?.code === 'err_formula_operand_type') {
    return describeOperandType(diagnostic.detail);
  }
  // A multi select picks more than one option from a single answer, by naming
  // them in one literal with a comma between — the only way to say it, since
  // the language has no list. The one-option example reads as the opposite.
  if (
    diagnostic?.code === 'err_formula_select_result_not_literal' &&
    kind === 'rel_tag_multi'
  ) {
    return 'A dropdown\u2019s formula must return its option names, like if(\u2026, "Senior, Mid", "Junior") \u2014 separate several with a comma.';
  }
  // One of these arrives per offending literal, so `if(c, "YES", "NO")` on a
  // Yes/No field reports both rather than only the first.
  if (diagnostic?.code === 'err_formula_result_unstorable') {
    return describeUnstorable(diagnostic.detail);
  }
  if (diagnostic?.code === 'err_formula_date_format_invalid') {
    return describeDateFormat(diagnostic.detail);
  }
  if (diagnostic?.code === 'err_formula_invalid_unit') {
    return describeInvalidUnit(diagnostic.detail);
  }
  if (diagnostic?.code === 'err_formula_date_needs_format') {
    return describeNeedsFormat(diagnostic.detail);
  }
  return (diagnostic?.code && DIAGNOSTIC_MESSAGES[diagnostic.code]) || FALLBACK;
};

/**
 * A field the formula reads that this one's scope does not reach. Not a
 * refusal: the rule saves, and on the offers where the input is missing the
 * value simply cannot be worked out — which is what the admin needs telling
 * before a recruiter meets it.
 *
 * Narrower by both office and department reads as one sentence, not two.
 *
 * `source` names the object the field lives on, because an offer formula reads
 * job and requisition fields too — unqualified, the sentence sends the admin to
 * widen an offer field that is already as wide as it goes.
 */
const SOURCE_NOUN: Record<string, string> = {
  job: 'job',
  requisition: 'requisition',
};

export const describeScopeWarning = ({
  label,
  source,
  reasons = [],
}: FormulaScopeWarning): string => {
  // Both halves off the MAPPED noun, not the raw source: a source this copy does
  // not know would otherwise fall back to "offer" and still announce itself, so
  // a referral field read "is an offer field on" — naming the wrong object,
  // which is the mistake this warning exists to stop.
  const named = source ? SOURCE_NOUN[source] : undefined;
  const noun = named ?? 'offer';
  const article = /^[aeiou]/.test(noun) ? 'an' : 'a';
  // "is a job field" rather than a clause of its own, which is the shape the
  // error-copy sheet already uses to say what kind of field is being named
  // ("{Field}" is a {type} field and can't be used in a formula). Only where it
  // says something: every field the admin can see in this drawer is an offer field.
  const whose = named ? `is ${article} ${noun} field on` : 'is on';

  if (reasons.includes('err_formula_field_unscoped')) {
    // A source with no noun names no form either, rather than sending the admin
    // to the offer form for a field that is not on it.
    if (source && !named)
      return `"${label}" is not on any form, so this value can never be calculated. Add "${label}" to a form, or map to a different field.`;

    return `"${label}" is not on any ${noun} form, so this value can never be calculated. Add "${label}" to ${article} ${noun} form, or map to a different field.`;
  }

  const narrower = [
    reasons.includes('err_formula_field_office_narrower') && 'office',
    reasons.includes('err_formula_field_department_narrower') && 'department',
  ].filter(Boolean);

  // The sheet's sentence says "offices or departments"; where the server has said
  // which of the two is narrower, that word goes in instead — the admin is
  // being asked to go and widen one of them.
  const where =
    narrower.length === 0
      ? 'offices or departments'
      : `${narrower.join('s or ')}s`;

  return `"${label}" ${whose} fewer ${where} than this field, so this value can\u2019t be calculated everywhere. Add "${label}" to the remaining ${where}.`;
};

/**
 * The refusals the editor can point a character at.
 *
 * Both of these name a quoted literal and now carry the offset of its opening
 * quote in the formula as the author typed it (core#6190). `position` is still
 * null when the offending value is not a quoted literal — `start_day(1, …)` is
 * refused with `detail.unit == "1"` — and those sentences stand on their own.
 *
 * The offset is checked against the text rather than trusted: the quoted run
 * at `position` has to be the literal the server named, or nothing is
 * underlined. A verdict is one debounce behind the box, and underlining the
 * wrong characters is worse than underlining none.
 */
const POSITIONED_LITERAL: Record<string, string> = {
  err_formula_date_format_invalid: 'format',
  err_formula_invalid_unit: 'unit',
};

export const diagnosticIssues = (
  diagnostics: FormulaDiagnostic[] | undefined,
  formula: string
): FormulaIssue[] =>
  (diagnostics ?? []).flatMap((diagnostic) => {
    const key = POSITIONED_LITERAL[diagnostic?.code ?? ''];
    const start = diagnostic?.position;
    if (!key || typeof start !== 'number' || start < 0) return [];
    const literal = diagnostic.detail?.[key];
    if (typeof literal !== 'string' || literal === '') return [];
    const quoted = `"${literal}"`;
    if (formula.slice(start, start + quoted.length) !== quoted) return [];
    return [{ message: describeDiagnostic(diagnostic), text: quoted, start }];
  });
